import { Controller } from "@hotwired/stimulus"

// One native dialog serves every product screenshot on an audience landing. The enlarged image
// receives its source only after a person asks for it, so adding another screenshot does not add
// a second eager image element or another modal to the document.
export default class extends Controller {
  static targets = ["dialog", "viewport", "image", "caption", "zoomLevel", "position"]

  connect() {
    this.pointers = new Map()
    this.items = Array.from(this.element.querySelectorAll("[data-image-lightbox-src]"))
    this.resetTransform()
  }

  open(event) {
    const trigger = event.currentTarget
    this.currentIndex = this.items.indexOf(trigger)
    this.showCurrentImage()
    this.dialogTarget.showModal()
  }

  previous() {
    this.showImageAt(this.currentIndex - 1)
  }

  next() {
    this.showImageAt(this.currentIndex + 1)
  }

  key(event) {
    if (!this.dialogTarget.open) return

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
    } else if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    }
  }

  showImageAt(index) {
    this.currentIndex = (index + this.items.length) % this.items.length
    this.showCurrentImage()
  }

  showCurrentImage() {
    const trigger = this.items[this.currentIndex]

    this.imageTarget.dispatchEvent(new CustomEvent("loadable:begin", {
      bubbles: true,
      detail: { source: trigger.dataset.imageLightboxSrc }
    }))
    this.imageTarget.src = trigger.dataset.imageLightboxSrc
    this.imageTarget.alt = trigger.dataset.imageLightboxAlt
    this.captionTarget.textContent = trigger.dataset.imageLightboxCaption
    this.positionTarget.textContent = `${this.currentIndex + 1} / ${this.items.length}`
    this.resetTransform()
  }

  close() {
    this.dialogTarget.close()
  }

  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  reset() {
    this.resetTransform()
    this.imageTarget.removeAttribute("src")
    this.imageTarget.alt = ""
    this.captionTarget.textContent = ""
    this.positionTarget.textContent = ""
    this.currentIndex = null
  }

  zoomWithWheel(event) {
    event.preventDefault()
    const factor = Math.exp(-event.deltaY * 0.002)
    this.zoomAt(this.scale * factor, event.clientX, event.clientY)
  }

  zoomIn() {
    this.zoomAtViewportCentre(this.scale * 1.35)
  }

  zoomOut() {
    this.zoomAtViewportCentre(this.scale / 1.35)
  }

  resetTransform() {
    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.pointers?.clear()
    this.pinch = null
    this.swipe = null
    this.renderTransform()
  }

  startGesture(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return

    event.preventDefault()
    try {
      this.viewportTarget.setPointerCapture(event.pointerId)
    } catch (_error) {
      // A synthetic test event or an already-lost touch can have no capturable pointer.
    }
    this.pointers.set(event.pointerId, this.point(event))
    if (this.pointers.size === 1 && event.pointerType === "touch" && this.scale === 1) {
      this.swipe = { pointerId: event.pointerId, ...this.point(event) }
    } else {
      this.swipe = null
    }
    if (this.pointers.size === 2) this.rememberPinch()
  }

  moveGesture(event) {
    const previous = this.pointers.get(event.pointerId)
    if (!previous) return

    event.preventDefault()
    this.pointers.set(event.pointerId, this.point(event))

    if (this.pointers.size === 2) {
      this.movePinch()
    } else if (this.pointers.size === 1 && this.scale > 1) {
      this.translateX += event.clientX - previous.x
      this.translateY += event.clientY - previous.y
      this.renderTransform()
    }
  }

  endGesture(event) {
    const current = this.pointers.get(event.pointerId)
    const swipe = this.swipe
    this.pointers.delete(event.pointerId)
    this.pinch = null
    this.swipe = null

    if (!current || !swipe || swipe.pointerId !== event.pointerId || this.scale !== 1) return

    const deltaX = current.x - swipe.x
    const deltaY = current.y - swipe.y
    if (Math.abs(deltaX) < 56 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return

    if (deltaX < 0) this.next()
    else this.previous()
  }

  imageLoaded() {
    this.renderTransform()
  }

  zoomAtViewportCentre(nextScale) {
    const viewport = this.viewportTarget.getBoundingClientRect()
    this.zoomAt(nextScale, viewport.left + (viewport.width / 2), viewport.top + (viewport.height / 2))
  }

  zoomAt(nextScale, clientX, clientY) {
    const scale = Math.min(5, Math.max(1, nextScale))
    const viewport = this.viewportTarget.getBoundingClientRect()
    const anchorX = clientX - viewport.left - (viewport.width / 2)
    const anchorY = clientY - viewport.top - (viewport.height / 2)
    const ratio = scale / this.scale

    this.translateX = anchorX - ((anchorX - this.translateX) * ratio)
    this.translateY = anchorY - ((anchorY - this.translateY) * ratio)
    this.scale = scale
    this.renderTransform()
  }

  rememberPinch() {
    const [first, second] = [...this.pointers.values()]
    this.pinch = {
      distance: Math.hypot(second.x - first.x, second.y - first.y),
      centre: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 }
    }
  }

  movePinch() {
    const [first, second] = [...this.pointers.values()]
    const distance = Math.hypot(second.x - first.x, second.y - first.y)
    const centre = { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 }
    if (!this.pinch || this.pinch.distance === 0) {
      this.rememberPinch()
      return
    }

    this.translateX += centre.x - this.pinch.centre.x
    this.translateY += centre.y - this.pinch.centre.y
    this.zoomAt(this.scale * (distance / this.pinch.distance), centre.x, centre.y)
    this.pinch = { distance: distance, centre: centre }
  }

  renderTransform() {
    if (!this.hasImageTarget) return

    this.clampTranslation()
    this.imageTarget.style.transform =
      `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`
    this.viewportTarget?.classList.toggle("is-zoomed", this.scale > 1)
    if (this.hasZoomLevelTarget) this.zoomLevelTarget.textContent = `${Math.round(this.scale * 100)}%`
  }

  clampTranslation() {
    if (!this.hasViewportTarget || !this.hasImageTarget) return

    const viewport = this.viewportTarget.getBoundingClientRect()
    const maxX = Math.max(0, ((this.imageTarget.offsetWidth * this.scale) - viewport.width) / 2)
    const maxY = Math.max(0, ((this.imageTarget.offsetHeight * this.scale) - viewport.height) / 2)
    this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX))
    this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY))
  }

  point(event) {
    return { x: event.clientX, y: event.clientY }
  }
}
